import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  publishInProgress,
  type ClientSiteFacts,
  type ClientSitePublish,
  type useClientPublishSiteFacts,
  type useClientSaveSiteFactsDraft
} from "../../data/clientFactsQueries";
import { canonicalValues, fieldValue, inputsFrom, type FormInputs } from "../../lib/facts/inputs";
import type { FactsField, FactsProblem, FactsSchema, FactsValues } from "../../lib/facts/types";
import { validateFactsValues } from "../../lib/facts/validate";
import { describeValue } from "../../lib/facts/words";
import { Callout } from "../../ui/Callout";
import { FieldControl, type ProblemsAt } from "../../ui/facts/FactsControls";
import { ProblemSentence } from "../../ui/facts/problemSentence";
import { formatWhen, inSentence } from "../clientOps/format";
import {
  CHECKED_LOCALLY,
  LOCKED_LINK,
  LOCKED_QUESTION,
  PUBLISH_LABEL,
  PUBLISH_PENDING,
  PUBLISH_STATUS,
  SAVE_LABEL,
  SAVE_PENDING,
  WAITING_FOR_FACTS_STATUS,
  blockedSentence,
  clientProblemSentence,
  refusalSentence
} from "./clientFactsWords";
import "./clientFacts.css";

/**
 * The client's facts form on Webbplatsens fakta, build round 19 (2026-10-08), decision 0503.
 *
 * **Built from the stored schema exactly as Petrovic's is**, with the same controls
 * (`app/ui/facts/FactsControls.tsx`) and the same conversion (`app/lib/facts/inputs.ts`). The one
 * difference is who may change what: a field is the client's only when the schema says
 * `editable: true`. Every other field, phone, e-mail and address always among them, is shown as text
 * with a way to ask Petrovic in the chat, and is sent as the latest published version holds it,
 * whatever a draft says. The server refuses any change to it by name (`PORTAL_SITE_FACTS_LOCKED`),
 * comparing against that same version, so sending anything else could only be refused.
 *
 * **What the form started from is what it saves against.** The client's own draft when the read gives
 * one, else the latest version; with the draft revision and the schema's SHA the read gave. A fresh
 * read replaces the form only when nothing typed would be lost, as on Petrovic's form. Otherwise the
 * form keeps what was typed and says that the stored facts have moved, with a way to start again.
 *
 * **Both buttons need a valid form.** `validateFactsValues` refuses before anything is sent, and the
 * database checks again; its problems are marked on the same fields. "Publicera på webbplatsen" sends
 * the form as it stands, not a saved draft: it is one act on the server, the next version and the
 * site's build together.
 *
 * The mutations are held by the page and not here, because a fresh read can rebuild this form and a
 * result line has to survive that.
 */

type SaveMutation = ReturnType<typeof useClientSaveSiteFactsDraft>;
type PublishMutation = ReturnType<typeof useClientPublishSiteFacts>;

interface FormBase {
  /** Changes when the stored schema, the draft's revision or the latest version does. */
  token: string;
  schema: FactsSchema;
  schemaSha: string;
  revision: number;
  /** What the form shows: the client's draft, else the latest version. */
  values: FactsValues;
  /** The latest version, which every field the client may not change is taken from. */
  published: FactsValues;
}

interface FormState {
  base: FormBase;
  inputs: FormInputs;
  /** Set by the first press of either button; from then on the form checks itself as it is typed in. */
  checked: boolean;
}

const isEditable = (field: FactsField) => field.editable === true;

function baseOf(facts: ClientSiteFacts): FormBase {
  return {
    token: `${facts.schema.sha}|${facts.revision ?? 0}|${facts.published.version}`,
    schema: facts.schema.document,
    schemaSha: facts.schema.sha,
    revision: facts.revision ?? 0,
    values: facts.draft?.values ?? facts.published.values,
    published: facts.published.values
  };
}

function startFrom(base: FormBase): FormState {
  return { base, inputs: inputsFrom(base.schema, base.values), checked: false };
}

/** What the form sends: the client's own fields as typed, every other field as the latest version holds it. */
function valuesOf(base: FormBase, inputs: FormInputs): Record<string, unknown> {
  return Object.fromEntries(
    base.schema.fields.map((field) => [
      field.key,
      isEditable(field) ? fieldValue(field, inputs[field.key]) : (base.published[field.key] ?? null)
    ])
  );
}

/** What the form started from, in the same terms, so an untouched form has no changes. */
function startValues(base: FormBase): Record<string, unknown> {
  return Object.fromEntries(
    base.schema.fields.map((field) => [
      field.key,
      (isEditable(field) ? base.values[field.key] : base.published[field.key]) ?? null
    ])
  );
}

/** Each pair once: the browser and the database usually name the same problem. */
function uniqueProblems(problems: FactsProblem[]): FactsProblem[] {
  const seen = new Set<string>();
  return problems.filter((problem) => {
    const id = `${problem.key}\u0000${problem.code}`;
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
}

/** Whether a problem's path is inside a field: its key, a day `hours[0]`, a list row `staff[1].name`. */
const belongsTo = (field: FactsField, path: string) => path === field.key || path.startsWith(`${field.key}[`);

/** Every path a control on the form answers for, so a problem at any other path is listed apart. */
function placedPaths(schema: FactsSchema, inputs: FormInputs): Set<string> {
  const paths = new Set<string>();
  for (const field of schema.fields.filter(isEditable)) {
    paths.add(field.key);
    const input = inputs[field.key];
    if (input?.kind === "hours") {
      input.days.forEach((_day, index) => paths.add(`${field.key}[${index}]`));
    }
    if (input?.kind === "list") {
      input.rows.forEach((_row, index) => {
        paths.add(`${field.key}[${index}]`);
        for (const item of field.item_fields ?? []) {
          paths.add(`${field.key}[${index}].${item.key}`);
        }
      });
    }
  }
  return paths;
}

/**
 * The build the client started, when it is worth a line: while it is on its way, always; once it
 * has ended, when this page started it or it ended within the hour. "Publicerad, syns på webbplatsen
 * om några minuter" read the next week would be a sentence that is no longer true, and a failure
 * Petrovic has been told about is his to follow up, not a standing line on the client's form.
 */
function statusWorthALine(
  last: ClientSitePublish | null,
  startedHere: string | null,
  waitingForFacts: boolean
): ClientSitePublish | null {
  if (!last) {
    return null;
  }
  if (publishInProgress(last) || last.id === startedHere) {
    return last;
  }
  // Build round 20, 2026-10-08: "Väntar på fakta" is true for as long as the read says so, so it is
  // not dropped after the hour the way a finished build's line is.
  if (waitingForFacts && (last.status === "failed" || last.status === "blocked_waiting_for_facts")) {
    return last;
  }
  const ended = Date.parse(last.completed_at ?? last.requested_at);
  return Date.now() - ended < 60 * 60_000 ? last : null;
}

/**
 * Whether the line says "Väntar på fakta" (build round 20, 2026-10-08): a publish held back because
 * the facts fail the site's new schema, or a build that failed while they do. A failure then is
 * Petrovic's facts to fill, not a fault the client should read as one.
 */
const waitsForFacts = (publish: ClientSitePublish, waitingForFacts: boolean) =>
  publish.status === "blocked_waiting_for_facts" || (publish.status === "failed" && waitingForFacts);

const STATUS_TONE = {
  queued: "info",
  dispatched: "info",
  succeeded: "ok",
  failed: "danger",
  blocked_waiting_for_facts: "info"
} as const;

/** A field only Petrovic changes: its value as text, and the way to ask for a change. */
function LockedField({ field, value, problems }: { field: FactsField; value: unknown; problems: FactsProblem[] }) {
  const id = useId();
  const empty = value === null || value === undefined;

  return (
    <div className="box-field" role="group" aria-labelledby={`${id}-label`} data-testid="facts-locked-field" data-key={field.key}>
      <strong id={`${id}-label`}>{field.label}</strong>
      <p className={empty ? "k-facts-locked-value quiet" : "k-facts-locked-value"} data-testid="facts-locked-value">
        {empty ? "Inte ifyllt" : describeValue(value, field)}
      </p>
      {problems.length > 0 ? (
        <p className="form-error" data-problem-key={field.key}>
          {problems.map((problem) => clientProblemSentence(problem.code, field)).join(" ")}
        </p>
      ) : null}
      <p className="line-hint" data-testid="facts-locked-line">
        {LOCKED_QUESTION} <Link to="/chat">{LOCKED_LINK}</Link>.
      </p>
    </div>
  );
}

export function ClientFactsForm({
  facts,
  tenantId,
  serviceId,
  save,
  publish
}: {
  facts: ClientSiteFacts;
  tenantId: string;
  serviceId: string;
  save: SaveMutation;
  publish: PublishMutation;
}) {
  const current = baseOf(facts);
  const [form, setForm] = useState<FormState>(() => startFrom(current));

  const values = valuesOf(form.base, form.inputs);
  const canonical = canonicalValues(form.base.schema, values);
  const dirty = canonical !== canonicalValues(form.base.schema, startValues(form.base));

  // A fresh read moved what the form started from. Take it when nothing typed is lost by it: when the
  // form holds no change, or holds exactly what is now stored (the save or publish just made).
  if (current.token !== form.base.token) {
    const sameAsStored =
      current.schemaSha === form.base.schemaSha && canonical === canonicalValues(current.schema, startValues(current));
    if (!dirty || sameAsStored) {
      setForm(startFrom(current));
    }
  }
  const stale = current.token !== form.base.token;

  const local = form.checked ? validateFactsValues(form.base.schema, values) : [];
  // The database's problems belong to the values it was sent, and stop being shown once those change.
  const sentSave = save.variables ? canonicalValues(form.base.schema, save.variables.values) : null;
  const sentPublish = publish.variables ? canonicalValues(form.base.schema, publish.variables.values) : null;
  const refused = [
    ...(save.error && sentSave === canonical ? save.error.problems : []),
    ...(publish.error && sentPublish === canonical ? publish.error.problems : [])
  ];
  const shown = uniqueProblems([...local, ...refused]);

  const placed = placedPaths(form.base.schema, form.inputs);
  const locked = form.base.schema.fields.filter((field) => !isEditable(field));
  const problemsAt: ProblemsAt = (path) => shown.filter((problem) => problem.key === path);
  const unplaced = shown.filter(
    (problem) => !placed.has(problem.key) && !locked.some((field) => belongsTo(field, problem.key))
  );

  const busy = save.isPending || publish.isPending;
  const failure = save.error ?? publish.error;
  const blocked = publish.data?.outcome === "blocked";
  const startedHere = publish.data?.outcome === "queued" ? publish.data.publish.id : null;
  // While a new press is on its way, or was answered "blocked", the line about the build before would
  // be read as the press's answer.
  const status = publish.isPending || blocked ? null : statusWorthALine(facts.last_publish, startedHere, facts.waiting_for_facts);
  const statusWaits = status ? waitsForFacts(status, facts.waiting_for_facts) : false;
  const draft = facts.draft;

  function send(kind: "save" | "publish") {
    setForm((state) => ({ ...state, checked: true }));
    // One answer on the form at a time: a saved draft's line is not left beside a publish's refusal.
    (kind === "save" ? publish : save).reset();
    if (validateFactsValues(form.base.schema, values).length > 0) {
      // Nothing is sent, and an earlier answer is not left on the screen beside the new refusal.
      (kind === "save" ? save : publish).reset();
      return;
    }
    const input = { tenantId, serviceId, values, schemaSha: form.base.schemaSha, revision: form.base.revision };
    if (kind === "save") {
      save.mutate(input);
    } else {
      publish.mutate(input);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    send("save");
  }

  return (
    <ProblemSentence.Provider value={clientProblemSentence}>
      <form
        className="k-form"
        aria-label={`Uppgifter för ${facts.name}`}
        noValidate
        onSubmit={submit}
        data-testid="client-facts-form"
      >
        {draft && !stale ? (
          <p className="line-hint" data-testid="client-facts-draft">
            Ett utkast är sparat {inSentence(formatWhen(draft.saved_at) ?? "")}
            {draft.saved_by ? ` av ${draft.saved_by}` : ""}. Det syns inte på webbplatsen förrän ni publicerar.
          </p>
        ) : null}
        {stale ? (
          <Callout
            tone="warn"
            testId="client-facts-stale"
            actions={
              <button type="button" className="button" onClick={() => setForm(startFrom(current))}>
                Börja om från det senaste
              </button>
            }
          >
            Uppgifterna har ändrats sedan ni började skriva. Det ni har skrivit finns kvar här men kan inte sparas över
            det nya.
          </Callout>
        ) : null}

        {form.base.schema.fields.map((field) =>
          isEditable(field) ? (
            <FieldControl
              key={field.key}
              field={field}
              input={form.inputs[field.key]}
              problemsAt={problemsAt}
              onChange={(next) => setForm((state) => ({ ...state, inputs: { ...state.inputs, [field.key]: next } }))}
            />
          ) : (
            <LockedField
              key={field.key}
              field={field}
              value={form.base.published[field.key]}
              problems={shown.filter((problem) => belongsTo(field, problem.key))}
            />
          )
        )}

        {unplaced.length > 0 ? (
          <ul className="form-error" data-testid="client-facts-other-problems">
            {unplaced.map((problem) => (
              <li key={`${problem.key}-${problem.code}`}>{clientProblemSentence(problem.code)}</li>
            ))}
          </ul>
        ) : null}
        {local.length > 0 ? (
          <p className="form-error" role="alert" data-testid="client-facts-checked">
            {CHECKED_LOCALLY}
          </p>
        ) : null}
        {failure ? (
          <p className="form-error" role="alert" data-testid="client-facts-refusal" data-code={failure.code}>
            {refusalSentence(failure)}
          </p>
        ) : null}
        {publish.data?.outcome === "blocked" ? (
          <Callout tone="warn" role="alert" testId="client-facts-blocked">
            {blockedSentence(publish.data.reason)}
          </Callout>
        ) : null}
        {save.isSuccess && !dirty && !stale ? (
          <p className="form-success" role="status" data-testid="client-facts-saved">
            Utkastet är sparat.
          </p>
        ) : null}
        {status ? (
          <div
            data-testid="client-facts-publish-status"
            data-status={status.status}
            data-waiting-for-facts={statusWaits ? "true" : "false"}
          >
            <Callout tone={statusWaits ? "info" : STATUS_TONE[status.status]} role="status">
              {statusWaits ? WAITING_FOR_FACTS_STATUS : PUBLISH_STATUS[status.status]}
            </Callout>
          </div>
        ) : null}

        <div className="form-actions">
          <button type="submit" className="button" disabled={busy || stale} data-testid="client-facts-save">
            {save.isPending ? SAVE_PENDING : SAVE_LABEL}
          </button>
          <button
            type="button"
            className="button is-primary"
            disabled={busy || stale}
            data-testid="client-facts-publish"
            onClick={() => send("publish")}
          >
            {publish.isPending ? PUBLISH_PENDING : PUBLISH_LABEL}
          </button>
        </div>
      </form>
    </ProblemSentence.Provider>
  );
}
